import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { CalendarEvent } from "../../hooks/useCalendarEvents";

export interface AddToProspoDialogProps {
  open: boolean;
  event: CalendarEvent | null;
  clients: any[];
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (event: CalendarEvent, clientId: string) => void;
}

/**
 * Picks the client a calendar event should be filed under.
 *
 * The import endpoint requires a client and attaches only that client's already
 * linked contacts, so the choice can't be inferred here — the advisor makes it.
 */
const AddToProspoDialog: React.FC<AddToProspoDialogProps> = ({
  open,
  event,
  clients,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [selectedClient, setSelectedClient] = useState<any>(null);

  // A fresh event means a fresh choice — otherwise the previous client sticks.
  useEffect(() => {
    if (open) setSelectedClient(null);
  }, [open, event?.id]);

  const options = useMemo(
    () => (clients || []).filter((c: any) => c?._id && c?.name),
    [clients],
  );

  const when = event
    ? `${dayjs(event.start).format("ddd D MMM YYYY")} · ${dayjs(event.start).format("hh:mm A")}`
    : "";

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Add to Prospo</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: "0.95rem" }}>
            {event?.title}
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: "0.85rem" }}>{when}</Typography>
          {event?.meetingMedium && (
            <Typography sx={{ color: "#6b7280", fontSize: "0.85rem" }}>
              {event.meetingMedium}
            </Typography>
          )}
        </Box>

        <Autocomplete
          options={options}
          value={selectedClient}
          onChange={(_, value) => setSelectedClient(value)}
          getOptionLabel={(option: any) => option?.name || ""}
          isOptionEqualToValue={(option: any, value: any) => option?._id === value?._id}
          disabled={submitting}
          renderInput={(params) => (
            <TextField {...params} label="Client" placeholder="Search clients…" autoFocus />
          )}
        />

        <Typography sx={{ color: "#6b7280", fontSize: "0.78rem", mt: 1.5 }}>
          Attendees already linked to this client are attached automatically.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selectedClient || submitting || !event}
          onClick={() => event && selectedClient && onConfirm(event, selectedClient._id)}
          sx={{ textTransform: "none" }}
        >
          {submitting ? "Adding…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToProspoDialog;
